'use client';

import { useState, useEffect } from 'react';
import { Wallet, Users } from 'lucide-react';
import { AppImage } from '@/components/common/AppImage';
import { Card } from '@/components/common/Card';
import { VerificationBadge } from '@/components/common/VerificationBadge';
import { UnverifiedBadge } from '@/components/common/UnverifiedBadge';
import { IconBadge } from '@/components/common/IconBadge';
import { PlayerActions } from './PlayerActions';
import { formatCurrency } from '@/lib/utils/format';
import { cn } from '@/lib/utils';
import { tournamentService } from '@/services/api/tournaments';
import { handleError } from '@/lib/utils/error';
import type { Player, Tournament } from '@/lib/types/auction';
import { useRouter } from 'next/navigation';

interface PlayerCardProps {
  player: Player;
  tournament?: Tournament;
  onSelect?: () => void;
  selected?: boolean;
  showActions?: boolean;
  onAction?: (updatedPlayer: Player | null) => void;
}

export function PlayerCard({
  player: initialPlayer,
  tournament: providedTournament,
  onSelect,
  selected,
  showActions = false,
  onAction,
}: PlayerCardProps) {
  const router = useRouter();
  const [player, setPlayer] = useState(initialPlayer);
  const [fetchedTournament, setFetchedTournament] = useState<Tournament | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(
    !providedTournament && !!player.tournamentId
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTournament = async () => {
      if (!providedTournament && player.tournamentId) {
        try {
          const tournamentData = await tournamentService.getById(
            player.tournamentId
          );
          setFetchedTournament(tournamentData);
        } catch (err) {
          setError(handleError(err, 'Loading Tournament'));
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTournament();
  }, [player.tournamentId, providedTournament]);

  const handlePlayerUpdate = (updatedPlayer: Player | null) => {
    if (updatedPlayer === null) {
      // Handle player deletion
      if (onAction) {
        onAction(null);
      }
      return;
    }
    setPlayer(updatedPlayer);
    if (onAction) {
      onAction(updatedPlayer);
    }
  };

  const tournament = providedTournament || fetchedTournament;
  const playerName = player.user?.name || 'Unknown Player';
  const playerImage = player.user?.profilePicture || '';

  /** Handle Click */
  const handleClick = (e: React.MouseEvent) => {
    // If the click is coming from the actions menu or its children, ignore it
    if ((e.target as HTMLElement).closest('[data-actions-menu]')) {
      return;
    }

    // If we're showing actions, only handle click if there's an onSelect handler
    if (showActions) {
      if (onSelect) {
        onSelect();
      }
      return;
    }

    // Otherwise, navigate to player details
    const url = `/player/${player.id}`;
    if (tournament) {
      sessionStorage.setItem(
        `tournament_${player.id}`,
        JSON.stringify(tournament)
      );
    }
    router.push(url);
  };

  return (
    <Card
      className={cn(
        'group flex h-full flex-col overflow-hidden backdrop-blur-none transition-all duration-300 hover:-translate-y-1 hover:shadow-lg',
        selected && 'ring-2 ring-primary-500',
        (onSelect || !showActions) && 'cursor-pointer'
      )}
      onClick={handleClick}
    >
      {/* Player Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <AppImage
          src={playerImage}
          alt={playerName}
          size="full"
          fallbackText={playerName}
          shape="square"
          gradient
          className="h-full w-full"
        />
        <div className="absolute inset-x-0 top-0 rounded-lg bg-gradient-to-b from-black/60 via-black/40 to-transparent p-2">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-xs font-medium text-primary-700">
              {player.status === 'sold' ? 'Sold' : 'Player'}
            </span>
            {tournament && !isLoading && !error && (
              <div data-actions-menu>
                <PlayerActions
                  player={player}
                  tournament={tournament}
                  onAction={handlePlayerUpdate}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Player Details */}
      <div className="flex flex-1 flex-col space-y-2 p-2">
        <div className="flex items-center gap-1.5">
          <h3 className="text-base font-semibold leading-tight text-gray-900 sm:text-lg">
            {playerName}
          </h3>
          <div className="flex items-center gap-1">
            {player.user?.emailVerified ? (
              <VerificationBadge />
            ) : (
              <UnverifiedBadge />
            )}
            {player.role === 'icon' && <IconBadge />}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-accent-green/5 to-accent-green/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-accent-green" />
            <span className="text-sm text-gray-700">
              {player.status === 'sold' ? 'Purchased For' : 'Base Price'}
            </span>
          </div>
          <span className="text-sm font-medium text-gray-900">
            {formatCurrency(
              player.status === 'sold' && player.soldPrice
                ? player.soldPrice
                : player.basePrice
            )}
          </span>
        </div>

        {player.status === 'sold' && player.team && (
          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-accent-orange/5 to-accent-orange/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary-600" />
              <span className="text-sm text-gray-700">Team</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {player.team.name}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
